/**
 * Whether a condition itself came back flagged. Its own grade, not its metrics —
 * the count on a condition's card is `flagged_count`, which the query supplies.
 */
const isFlagged = (evaluation) => evaluation === 'High' || evaluation === 'Medium';
const EVALUATION_ORDER = {
    High: 0,
    Medium: 1,
    Low: 2,
    Unevaluated: 3,
};
const COLOR_BY_CONDITION_EVALUATION = {
    High: 'high',
    Medium: 'moderate',
    Low: 'low',
    Unevaluated: 'low',
};
/** Organs have no gray state, so a neutral or clear marker reads as green. */
const MARKER_FLAG_LEVEL = {
    flag: 'high',
    borderline: 'moderate',
    neutral: 'low',
    clear: 'low',
};
const FLAG_LEVEL_RANK = {
    low: 0,
    moderate: 1,
    high: 2,
};
// Both lookups key on DB strings, so an unknown value reads as green.
const pickWorstColor = (levels) => levels.reduce((worst, level = 'low') => FLAG_LEVEL_RANK[level] > FLAG_LEVEL_RANK[worst] ? level : worst, 'low');
/**
 * The organ's colour: its single worst condition evaluation or marker, so one
 * red result turns the organ red however many green ones sit beside it.
 * @param {GutBodyConnectionsCondition[]} conditions - The conditions this view lists.
 * @param {GutBodyConnectionsMarker[]} markers - The condition markers this view lists.
 * @returns {GutBodyConnectionsFlagLevel} Red over orange over green.
 */
export function resolveGutBodyConnectionsFlagLevel(conditions, markers = []) {
    return pickWorstColor([
        ...conditions.map(condition => COLOR_BY_CONDITION_EVALUATION[condition.evaluation]),
        ...markers.map(marker => MARKER_FLAG_LEVEL[marker.evaluation_meaning]),
    ]);
}
/** Unordered markers sort last, as the query's `ORDER BY` leaves them. */
const byDisplayOrder = (a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity) ||
    a.display_title.localeCompare(b.display_title);
/**
 * Groups organ/condition and organ/marker join rows into organs and resolves
 * each one's color. Organs sort by `priority` ascending, conditions
 * High -> Medium -> Low -> Unevaluated then alphabetically, markers by `display_order`.
 * @param {GutBodyConnectionsOrganConditionRow[]} rows - One row per organ/condition pair.
 * @param {GutBodyConnectionsOrganMarkerRow[]} markerRows - One row per organ/marker pair.
 * @returns {GutBodyConnectionsOrgan[]} Grouped organs for the body component.
 */
export function buildGutBodyConnectionsOrgans(rows, markerRows = []) {
    const organsMap = new Map();
    const seenConditions = new Map();
    const seenMarkers = new Map();
    // An organ carrying only markers still has to render, so either row creates it.
    const organFor = (row) => {
        let organ = organsMap.get(row.organ_id);
        if (!organ) {
            organ = {
                id: row.organ_id,
                lookup_code: row.organ_lookup_code,
                display_name: row.organ_display_name,
                priority: row.organ_priority,
                about: row.organ_about,
                conditions: [],
                markers: [],
                flagged_count: 0,
                flag_level: 'low',
            };
            organsMap.set(row.organ_id, organ);
            seenConditions.set(row.organ_id, new Set());
            seenMarkers.set(row.organ_id, new Set());
        }
        return organ;
    };
    for (const row of rows) {
        const organ = organFor(row);
        // A widened join could repeat a condition and shift the organ's colour.
        const seen = seenConditions.get(row.organ_id);
        if (seen.has(row.condition_id))
            continue;
        seen.add(row.condition_id);
        organ.conditions.push({
            id: row.condition_id,
            condition: row.condition,
            display_name: row.condition_display_name,
            evaluation: row.evaluation,
            flagged_count: row.condition_flagged_count,
            total_count: row.condition_total_count,
            marked_in_survey: row.marked_in_survey,
            sensitive: row.sensitive,
        });
        if (isFlagged(row.evaluation))
            organ.flagged_count += 1;
    }
    for (const row of markerRows) {
        const organ = organFor(row);
        const seen = seenMarkers.get(row.organ_id);
        if (seen.has(row.marker_id))
            continue;
        seen.add(row.marker_id);
        organ.markers.push({
            id: row.marker_id,
            display_title: row.marker_display_title,
            display_order: row.marker_display_order,
            evaluation: row.evaluation,
            evaluation_meaning: row.evaluation_meaning,
        });
    }
    const organs = Array.from(organsMap.values());
    for (const organ of organs) {
        organ.conditions.sort((a, b) => EVALUATION_ORDER[a.evaluation] - EVALUATION_ORDER[b.evaluation] ||
            a.display_name.localeCompare(b.display_name));
        organ.markers.sort(byDisplayOrder);
        organ.flag_level = resolveGutBodyConnectionsFlagLevel(organ.conditions, organ.markers);
    }
    return organs.sort((a, b) => a.priority - b.priority);
}
//# sourceMappingURL=buildGutBodyConnectionsOrgans.js.map